import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import type { ICardTableRowWrapper, ITableRow, ITableWithCards, ITableExpandedRow } from '@/components/common/types';

import { media } from '@/styles/theme';
import { ChevronUp } from '@/assets/icons';
import { classList } from '@/helpers';

const TableCard = ({ children, className }: ITableWithCards): React.ReactElement => (
  <Container className={className}>{children}</Container>
);

const CardTableRowsWrapper = ({
  children,
  minWidth,
  isCollapsable = false,
  firstBgColor,
  secondBgColor,
  activeBorderColor,
}: ICardTableRowWrapper): React.ReactElement => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

  return (
    <RowsWrapper
      minWidth={minWidth}
      firstBgColor={firstBgColor}
      secondBgColor={secondBgColor}
      isCollapsed={isCollapsed}
      activeBorderColor={activeBorderColor}
    >
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            collapsed: isCollapsed,
            setIsCollapsed: setIsCollapsed,
            isCollapsable: isCollapsable,
          });
        }
        return child;
      })}
    </RowsWrapper>
  );
};

const TableRow = ({ children, collapsed, setIsCollapsed, isCollapsable, bgColor }: ITableRow): React.ReactElement => (
  <Row className={classList(collapsed && 'collapsed', isCollapsable && 'collapsable')} bgColor={bgColor}>
    {children}
    {isCollapsable && <ExpandRowButton onClick={() => setIsCollapsed && setIsCollapsed(!collapsed)} />}
  </Row>
);

const TableExpandedRow = ({ children, collapsed, bgColor }: ITableExpandedRow): React.ReactElement => (
  <ExpandedRow className={`${collapsed ? 'collapsed' : ''}`} bgColor={bgColor}>
    {children}
  </ExpandedRow>
);

const Container = styled.div`
  margin-top: 10px;
  overflow-y: auto;
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const Cell = styled.div<{ width?: string; minWidth?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  ${({ minWidth }) => (minWidth ? `min-width: ${minWidth}` : '')};
  ${({ width }) => (width ? `width: ${width}` : '')};
`;

const CellHeader = styled.div`
  flex-grow: 1;
  padding-bottom: 4px;
  padding-right: 30px;
  margin-right: 8px;
  font-weight: 700;
  font-size: 12px;
  line-height: 21px;
  color: #4b5563;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  ${media.tablet} {
    padding-right: 0;
  }
`;

const CellValue = styled.div`
  height: 41px;
  max-height: 41px;
  margin-right: 8px;

  font-weight: 400;
  font-size: 12px;
  line-height: 21px;
  color: #374151;
  text-overflow: ellipsis;
  overflow: hidden;

  ${media.tablet} {
    padding-right: 0;
  }
`;

const Row = styled.div<{ bgColor?: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
  position: relative;
  padding: 14px 10px 15px 12px;
  background-color: ${({ bgColor }) => bgColor && bgColor};

  &.collapsable {
    padding-right: 40px;
  }
`;

const RowsWrapper = styled.div<{
  minWidth?: number;
  firstBgColor?: string;
  secondBgColor?: string;
  isCollapsed?: boolean;
  activeBorderColor?: string;
}>`
  ${({ firstBgColor, secondBgColor }) => {
    if (firstBgColor && secondBgColor)
      return css`
        background-color: ${firstBgColor};

        &:nth-child(2n) {
          background-color: ${secondBgColor};
        }
      `;

    return 'background-color: #f9fafb';
  }}

  ${({ isCollapsed, activeBorderColor }) =>
    !isCollapsed &&
    css`
      border: ${activeBorderColor} 1px solid;
      border-radius: 8px;

      & > * {
        border-radius: 8px;
      }
    `};

  ${media.tablet} {
    ${({ minWidth }) => minWidth ?? `min-width: ${minWidth}px;`};

    &:nth-child(2n) {
      > ${Row} {
        background-color: #fff;
      }
    }
  }
`;

const ExpandedRow = styled.div<{ bgColor?: string }>`
  position: relative;
  padding: 14px 16px 20px 12px;
  background-color: ${({ bgColor }) => bgColor || '#fff'};

  &.collapsed {
    display: none;
  }

  ${media.tablet} {
    display: none;
  }
`;

const ExpandedRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding-top: 20px;

  &:first-child {
    padding-top: 0;
  }
`;

const Title = styled.div`
  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
  color: #4b5563;
`;

const ExpandRowButton = styled.div`
  position: absolute;
  top: 28px;
  right: 15px;
  width: 16px;
  height: 32px;

  background-image: url(${ChevronUp});
  background-size: 15px;
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;

  transition: transform 0.3s ease-in-out;

  .collapsed & {
    transform: rotate(180deg);
  }

  ${media.tablet} {
    display: none !important;
  }
`;

TableCard.CardTableRowsWrapper = CardTableRowsWrapper;
TableCard.Row = Row;
TableCard.Cell = Cell;
TableCard.CellHeader = CellHeader;
TableCard.CellValue = CellValue;
TableCard.TableRow = TableRow;
TableCard.TableExpandedRow = TableExpandedRow;
TableCard.ExpandedRowContainer = ExpandedRowContainer;
TableCard.Title = Title;

export { TableCard };
